import { fetchUtils } from 'react-admin';
import { apiUrl } from '../config/api';

// Cache to prevent duplicate requests
const requestCache = new Map<string, Promise<any>>();

// Flag to prevent multiple simultaneous refresh token requests
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Function for delay with exponential backoff
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Function to refresh access token
const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  isRefreshing = true;
  const refreshToken = localStorage.getItem('refresh_token');
  
  if (!refreshToken) {
    isRefreshing = false;
    refreshPromise = null;
    return null;
  }
  
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (!response.ok) {
        // Refresh token is invalid or expired
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        return null;
      }
      
      const data = await response.json();
      
      if (!data.success || !data.data?.token) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        return null;
      }
      
      // Save new access token
      localStorage.setItem('token', data.data.token);
      
      // If new refresh token is provided, save it (token rotation when close to expiration)
      if (data.data.refresh_token) {
        localStorage.setItem('refresh_token', data.data.refresh_token);
      }
      
      return data.data.token;
    } catch (error) {
      // Don't log full error object - may contain sensitive token data
      console.error('Error refreshing token:', error instanceof Error ? error.message : 'Unknown error');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
};

// Enhanced HTTP client with rate limiting handling
export const httpClient = async (url: string, options: any = {}, retryCount = 0): Promise<any> => {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second
  
  // Create key for request caching
  const cacheKey = `${url}_${JSON.stringify(options)}`;
  
  // Check if such request is already in progress
  if (requestCache.has(cacheKey)) {
    return requestCache.get(cacheKey);
  }
  
  const makeRequest = async (): Promise<any> => {
    if (!options.headers) {
      options.headers = new Headers({ Accept: 'application/json' });
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      options.headers.set('Authorization', `Bearer ${token}`);
    }
    
    try {
      const response = await fetchUtils.fetchJson(url, options);
      requestCache.delete(cacheKey); // Remove from cache after successful request
      return response;
    } catch (error: any) {
      // Handle 429 (Too Many Requests) error
      if (error.status === 429 && retryCount < maxRetries) {
        const delayTime = baseDelay * Math.pow(2, retryCount) + Math.random() * 1000;
        console.warn(`Received 429 error. Retrying in ${delayTime}ms (attempt ${retryCount + 1}/${maxRetries})`);
        
        await delay(delayTime);
        return httpClient(url, options, retryCount + 1);
      }
      
      // Handle 401 (Unauthorized) - try to refresh token
      if (error.status === 401) {
        // Try to refresh the access token
        const newToken = await refreshAccessToken();
        
        if (newToken) {
          // Update authorization header with new token
          options.headers.set('Authorization', `Bearer ${newToken}`);
          
          // Retry the original request with new token
          try {
            const response = await fetchUtils.fetchJson(url, options);
            requestCache.delete(cacheKey);
            return response;
          } catch (retryError: any) {
            // If retry also fails, redirect to login
            if (retryError.status === 401 || retryError.status === 403) {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              window.location.href = '/admin/#/login';
              throw new Error('Session expired. Please log in again.');
            }
            throw retryError;
          }
        } else {
          // Refresh token failed, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/admin/#/login';
          throw new Error('Session expired. Please log in again.');
        }
      }
      
      // Handle 403 (Forbidden)
      if (error.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/admin/#/login';
        throw new Error('Access denied. Please log in again.');
      }
      
      if (error.status === 500) {
        throw new Error('Server error. Please try again later.');
      }
      
      if (error.status === 404) {
        throw new Error('Resource not found.');
      }
      
      // Remove from cache on error
      requestCache.delete(cacheKey);
      throw error;
    }
  };
  
  const requestPromise = makeRequest();
  requestCache.set(cacheKey, requestPromise);
  
  return requestPromise;
};

// Clear cache function
export const clearRequestCache = () => {
  requestCache.clear();
};
